<script>
  import { fetchEventSource } from '@microsoft/fetch-event-source';
  import VuePdfEmbed from 'vue-pdf-embed';
  import { useWindowSize } from '@vueuse/core';
  import PdfViewer from '@/pages/CPG/PDFViewer.vue';
  import MarkdownIt from 'markdown-it'
  import { SunIcon, MoonIcon } from 'lucide-vue-next'
  import { LoopingRhombusesSpinner } from 'epic-spinners'
  import BookSelection from './BookSelection.vue'
  import { getBooks } from '@/services/getbookServices';
  import FeedbackOverlay from '@/components/Feedback.vue';
  import NewPortalNotice from '@/components/NewPortalNotice.vue';
  import MahtasMark from '@/components/MahtasMark.vue';
  import { v4 as uuidv4 } from 'uuid'
  import StreamingService from '@/services/streaming-service.js';
  import { debounce } from 'lodash'; // Add this import

  const md = new MarkdownIt({
    breaks: true,
    typographer: true,
    linkify: true
  }).enable(['list', 'emphasis', 'heading'])
  
  // Create a debounced version of startStream outside the component
  const debouncedStartStream = debounce(async function(streamingService, payload, callbacks) {
    try {
      await streamingService.startStreaming(payload, callbacks);
    } catch (error) {
      console.error("Debounced stream error:", error);
      if (callbacks.onError) {
        callbacks.onError(error);
      }
    }
  }, 1500, { leading: true, trailing: false });

  export default {
    name: 'CPGChat',
    components: {
      PdfViewer,
      LoopingRhombusesSpinner,
      BookSelection,
      FeedbackOverlay,
      NewPortalNotice,
      MahtasMark,
      SunIcon,
      MoonIcon
    },
  
    setup() {
      const { width } = useWindowSize();
      return { width };
    },
      
    data() {
      return {
        isDarkMode: localStorage.getItem('theme') === 'dark',
        appVersion: process.env.VUE_APP_VERSION || '3.1.1',
        userQuery: '',
        Query: '',
        streamStatus: '',
        isStreaming: false,
        isLoading: false,
        isImageLoading: false,
        abortController: null,
        requestLock: false,
        currentStream: null,
        lastRequestTime: 0,
        minRequestInterval: 1000,
        requestCache: new Map(),
        requestCacheTimeout: 5000,
        retryCount: 0,
        maxRetries: 3,
        retryDelay: 1000,
        aiResponse: {
          Response: '',
          Filename: '',
          Page: '',
          URL: '',
          Suggestions: {},
          image_transcript: ''
        },
        hold_image_transcript: '',
        CPG_ID: '',
        Query_UUID: '',
        full_string: '',
        isPdfOpen: false,
        pdfSource: null,
        pdfPage: 1,
        book_data: [],
        all_books: [],
        showBookSelection: false,
        searchType: 'all',
        COOLDOWN_PERIOD: 0,
        sessionRequestCount: 0,
        SESSION_REQUEST_LIMIT: 99999999,
        limit_messages: '',
        showScrollButton: false,
        test_fullresponse: [],
        past_input: [],
        queryCounters: {},
        queryTimestamps: {},
        maxTrackedQueries: 100,
        streamingService: null,
        responseObject: {},
        fullStringReturn: '',
        selectedImage: null,
        selectedImageFile: null,
        imagePreview: null,
        fileInput: null,
        lastSentImagePreview: null,
        showMedGemmaCard: false,
      }
    },

    created() {
      // Initialize the streaming service with an adapter for the Scribe API
      this.streamingService = new StreamingService({
        processAICommandStream: this.processAICommandStream.bind(this)
      }, true); // Set to true for debug mode

      this.periodicCleanup();
      this.applyTheme();
    },

    computed: {
      isMobile() {
        return this.width < 768;
      },
      
      pdfHeight() {
        return this.isMobile ? '90vh' : '100%';
      },

      renderedMarkdown() {
        if (!this.aiResponse.Response) return '';

        if(this.aiResponse.Response == "Unfortunately, I wasn't able to find the relevant information from the CPG database. Would you like me to search in Worldwide Search?"){
          this.aiResponse.Response = "Sorry, the question is out of CPG handbook content"
          this.aiResponse.Suggestions = null
          this.aiResponse.Filename = {}
        }

        let processedContent

        if (Array.isArray(this.aiResponse.Response)) {
          processedContent = this.aiResponse.Response.join('\n');
        } else if (typeof this.aiResponse.Response === 'string' && this.aiResponse.Response.length) {
          processedContent = this.aiResponse.Response;
        }

        processedContent = processedContent
        .replace(/\\n/g, '\n')                    // Convert literal \n to actual newlines
        .replace(/\n{3,}/g, '\n\n')              // Reduce multiple newlines to max 2
        .replace(/\n\n(\d+\.)/g, '\n\n\n$1')     // Add spacing before numbered lists
        .replace(/(\*\*.*?\*\*:)\n\s*/g, '$1 ')  // Clean spacing after bold headers
        .replace(/\n\s*-\s*/g, '\n- ')           // Normalize list item spacing
        .trim();
        
        return md.render(processedContent);
      },

      renderedImageTranscript(){
        if (!this.aiResponse.image_transcript) return '';

        let processedContent = this.aiResponse.image_transcript
        
        return md.render(processedContent);
      }
    },

    watch: {
      searchType(newValue) {
        if(newValue === 'all') {
          this.CPG_ID = '';
          this.book_data = [];
          this.pdfSource = '';
        }
      },
      'aiResponse.image_transcript'(newValue) {
        // Reset the card visibility state when new transcript is received
        this.showMedGemmaCard = false;
      }
    },

    beforeUnmount() {
      this.clearImage();
      this.resetStreamState();
      this.stopStreamRequest();
      this.reset_response();
      this.queryCounters = {};
      this.queryTimestamps = {};
      if (this.requestCacheTimeout) {
        clearTimeout(this.requestCacheTimeout);
      }
    },
  
    methods: {
      toggleTheme() {
        this.isDarkMode = !this.isDarkMode;
        localStorage.setItem('theme', this.isDarkMode ? 'dark' : 'light');
        this.applyTheme();
      },

      applyTheme() {
        document.documentElement.classList.toggle('dark-mode', this.isDarkMode);
        document.body.classList.toggle('dark-mode', this.isDarkMode);
      },
      
      toggleMedGemmaCard() {
        this.showMedGemmaCard = !this.showMedGemmaCard;
      },
      
      periodicCleanup() {
        const now = Date.now();
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
        
        Object.keys(this.queryTimestamps).forEach(query => {
          if (now - this.queryTimestamps[query] > sevenDaysInMs) {
            delete this.queryCounters[query];
            delete this.queryTimestamps[query];
          }
        });
      },
      
      clearBookSelection() {
        this.book_data = [];
        this.CPG_ID = '';
        this.pdfSource = '';
        this.showBookSelection = false;
      },

      handleBookSelection(selectedBook) {
        if (selectedBook) {
          this.book_data = selectedBook;
          this.CPG_ID = selectedBook.id;
          this.pdfSource = selectedBook.url;
        }
        this.showBookSelection = false;
      },
      
      resetStreamState() {
        console.log('Resetting stream state');
        this.userQuery = '';
        this.isStreaming = false;
        this.requestLock = false;
        this.streamStatus = '';
        this.isLoading = false;
        this.isImageLoading = false;
        this.currentStream = null;
        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }
      },

      isDuplicateRequest(query) {
        const requestKey = JSON.stringify({
          query: query,
          timestamp: Date.now()
        });
        
        if (this.requestCache.has(requestKey)) {
          console.log('Duplicate request detected');
          return true;
        }
        
        // Cache the request
        this.requestCache.set(requestKey, true);
        
        // Clean up old cache entries
        setTimeout(() => {
          this.requestCache.delete(requestKey);
        }, this.requestCacheTimeout);
        
        return false;
      },

      isRequestThrottled() {
        const now = Date.now();
        const timeSinceLastRequest = now - this.lastRequestTime;
        
        if (timeSinceLastRequest < this.minRequestInterval) {
          console.log(`Request throttled. Please wait ${Math.ceil((this.minRequestInterval - timeSinceLastRequest) / 1000)} seconds`);
          return true;
        }
        
        this.lastRequestTime = now;
        return false;
      },

      async handleSubmit() {
        // Always clear last sent image preview before starting a new query
        this.showMedGemmaCard = false;
        this.lastSentImagePreview = null;
        console.log('Handle submit called with state:', {
          isStreaming: this.isStreaming,
          requestLock: this.requestLock,
          isLoading: this.isLoading
        });

        // For selective search, require query
        if (this.CPG_ID && !this.userQuery.trim()) {
          console.log('Empty query for selective search, returning');
          return;
        }

        // For local search, require either query or image
        if (!this.CPG_ID && !this.userQuery.trim() && !this.selectedImage) {
          console.log('Empty query and no image for local search, returning');
          return;
        }

        // If image is uploaded but query is empty, reject
        if (!this.CPG_ID && this.selectedImage && !this.userQuery.trim()) {
          alert('Please enter a query prompt when uploading an image.');
          return;
        }

        if (this.isStreaming) {
          console.log('Stream already in progress, returning');
          return;
        }

        // Check for duplicate request
        if (this.isDuplicateRequest(this.userQuery)) {
          return;
        }

        // Check for throttling
        if (this.isRequestThrottled()) {
          return;
        }

        // Set initial states
        this.isLoading = true;

        // Only keep the last 5 queries in the history
        if (this.past_input.length >= 5) {
          this.past_input.shift();
        }
        this.past_input.push(this.userQuery);

        console.log("Starting new request with query:", this.userQuery);

        // Check if this query has been asked too frequently
        if (!this.checkPastInputs(this.userQuery)) {
          this.resetStreamState();
          return;
        }
        
        this.Query = this.userQuery;
        // Store the current query for display
        const currentQuery = this.userQuery;
        
        this.startStream(currentQuery);
        // Move current image to last sent preview as data URL, then clear
        if (this.selectedImageFile) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.lastSentImagePreview = e.target.result;
            this.clearImage();
          };
          reader.readAsDataURL(this.selectedImageFile);
        } else {
          this.clearImage();
        }
      },

      checkPastInputs(query) {
        // Get current timestamp
        const now = Date.now();
        
        // Initialize counter for this query if it doesn't exist
        if (!this.queryCounters[query]) {
          this.queryCounters[query] = 0;
        }
        
        // Check if the query was asked before and how long ago
        const lastAsked = this.queryTimestamps[query] || 0;
        const hoursSinceLastAsked = (now - lastAsked) / (1000 * 60 * 60);
        
        // Reset counter if it's been more than 24 hours since last asked
        if (hoursSinceLastAsked > 24) {
          this.queryCounters[query] = 0;
        }
        
        // Increment the counter and update timestamp
        this.queryCounters[query]++;
        this.queryTimestamps[query] = now;
        
        // Run cleanup if we have too many tracked queries
        this.cleanupQueryTracking();
        
        // Check if the query has been asked too many times
        if (this.queryCounters[query] >= 3) {
          alert("You have asked this question multiple times. Please try rephrasing your question or ask something different.");
          
          // Reset the counter to allow the user to try again later
          this.queryCounters[query] = 0;
          return false;
        }
        
        return true;
      },

      cleanupQueryTracking() {
        const now = Date.now();
        const keys = Object.keys(this.queryTimestamps);
        
        // If we haven't exceeded the limit, no need to clean up
        if (keys.length <= this.maxTrackedQueries) {
          return;
        }
        
        // Create an array of entries with their age
        const entries = keys.map(key => ({
          query: key,
          timestamp: this.queryTimestamps[key],
          age: now - this.queryTimestamps[key]
        }));
        
        // Sort by age (oldest first)
        entries.sort((a, b) => b.age - a.age);
        
        // Remove the oldest entries to get back under the limit
        const entriesToRemove = entries.slice(0, entries.length - this.maxTrackedQueries);
        
        for (const entry of entriesToRemove) {
          delete this.queryCounters[entry.query];
          delete this.queryTimestamps[entry.query];
        }
      },
      
      handleSuggestionClick(suggestion) {
        this.userQuery = suggestion;
        this.handleSubmit();
      },

      checkCooldown() {
        const now = Date.now();
        const timeSinceLastRequest = now - this.lastRequestTime;
        
        if (timeSinceLastRequest < this.COOLDOWN_PERIOD) {
          const remainingTime = Math.ceil((this.COOLDOWN_PERIOD - timeSinceLastRequest) / 1000);
          this.limit_messages = `Please wait ${remainingTime} seconds before next request`;
          return false;
        }
        
        this.lastRequestTime = now;
        return true;
      },

      checkSessionLimit() {
        if (this.sessionRequestCount >= this.SESSION_REQUEST_LIMIT) {
          this.limit_messages = `Session limit reached for the session. Try again later :)`;
          return false;
        }
        
        this.sessionRequestCount++;
        return true;
      },
  
      // Updated method to work with StreamingService
      async processAICommandStream(payload, callback) {
        const endpoint = this.CPG_ID 
          ? 'https://langserve-copilot-dp7nm56cca-as.a.run.app/v4/cpg/selective_search'
          : 'https://langserve-copilot-dp7nm56cca-as.a.run.app/v7/cpg/local_search_azure';

        try {
          let requestBody;
          let headers = {
            'Accept': 'text/event-stream'
          };

          if (this.CPG_ID) {
            // For selective search (v4), use JSON
            headers['Content-Type'] = 'application/json';
            requestBody = JSON.stringify(payload);
          } else {
            // For local search (v5), use FormData
            const formData = new FormData();
            formData.append('query', payload.query);
            formData.append('is_testing', payload.is_testing);
            formData.append('cpg_location', 'askcpg')
            
            if (this.selectedImage) {
              // Convert the image URL to a Blob
              const response = await fetch(this.selectedImage);
              const blob = await response.blob();
              formData.append('file', blob, 'image.jpg');
            }
            requestBody = formData;
          }

          const response = await fetch(endpoint, {
            method: 'POST',
            headers: headers,
            body: requestBody
          });

          // Set up reader for the response body stream
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            // Decode the chunk and add to buffer
            buffer += decoder.decode(value, { stream: true });
            
            // Process complete lines from the buffer
            const lines = buffer.split('\n');
            // Keep the last (potentially incomplete) line in the buffer
            buffer = lines.pop() || '';
            
            // Process each complete line
            for (const line of lines) {
              if (line.trim()) {
                callback(line);
              }
            }
          }
          
          // Process any remaining data in the buffer
          if (buffer.trim()) {
            callback(buffer);
          }
        } catch (error) {
          console.error('Streaming error:', error);
          throw error;
        }
      },

      async startStream(query) {
        console.log("Starting stream for query:", query, "with state:", {
          isStreaming: this.isStreaming,
          requestLock: this.requestLock
        });

        if (this.isStreaming) {
          console.log("Stream already in progress. Returning.");
          return;
        }

        try {
          this.isStreaming = true;
          this.requestLock = true;
          this.reset_response();
          this.streamStatus = 'Initiating search...';
          
          const payload = this.CPG_ID 
            ? {
                query,
                book_id: this.CPG_ID,
                is_testing: false
              }
            : {
                query,
                is_testing: false
              };
          
          console.log('Sending payload to streaming service:', payload);

          const callbacks = {
            onStart: () => {
              console.log("Stream started");
            },
            
            onChunk: (chunkData) => {
              try {
                let processedData = chunkData;
                if (typeof chunkData === 'string' && chunkData.startsWith('data: ')) {
                  try {
                    const jsonStr = chunkData.substring(6).trim();
                    const jsonData = JSON.parse(jsonStr);
                    this.processStreamPart(jsonData);
                  } catch (parseError) {
                    console.error('Error parsing SSE JSON:', parseError);
                    this.handleStreamError(parseError);
                  }
                }
              } catch (error) {
                console.error('Error in chunk processing:', error);
                this.handleStreamError(error);
              }
            },
            
            onInterface: (interfaceType) => {
              console.log("Interface type:", interfaceType);
            },
            
            onSystem: (systemMsg) => {
              this.streamStatus = systemMsg || 'Processing...';
            },
            
            onContent: (content) => {
              if (content) {
                console.log("Content received:", content);
              }
            },
            
            onFinish: () => {
              console.log("Stream finished");
              this.handleStreamClose();
            },
            
            onError: async (error) => {
              console.error("Stream error in callbacks:", error);
              if (this.retryCount < this.maxRetries) {
                this.retryCount++;
                console.log(`Retrying request (${this.retryCount}/${this.maxRetries})...`);
                await new Promise(resolve => setTimeout(resolve, this.retryDelay * this.retryCount));
                this.startStream(query);
              } else {
                this.handleStreamError(error);
              }
            }
          };

          // Create new AbortController for this request
          this.abortController = new AbortController();

          // Start the stream
          this.currentStream = await this.streamingService.startStreaming(payload, callbacks);
          console.log("Stream initiated successfully");

        } catch (error) {
          console.error("Error in startStream:", error);
          if (this.retryCount < this.maxRetries) {
            this.retryCount++;
            console.log(`Retrying request (${this.retryCount}/${this.maxRetries})...`);
            await new Promise(resolve => setTimeout(resolve, this.retryDelay * this.retryCount));
            this.startStream(query);
          } else {
            this.handleStreamError(error);
          }
        }
      },
      
      processStreamPart(jsonData) {
        if (!jsonData.query_uuid && !jsonData.image_transcript && !jsonData.system_data) {
          this.fullStringReturn += Object.values(jsonData).join('');
        }

        if (jsonData.query_uuid) {
          this.Query_UUID = jsonData.query_uuid;
        }
        
        if (jsonData.Response) {
          this.streamStatus = 'Receiving response...';
          if(jsonData.Response !== 'Response'){
            this.aiResponse.Response += jsonData.Response;
          }
        }
        if(jsonData.image_transcript){
          this.aiResponse.image_transcript += jsonData.image_transcript;
          this.hold_image_transcript = jsonData.image_transcript;
        }
      },
      
      stopStreamRequest() {
        if (this.streamingService) {
          this.streamingService.stopStreaming();
          this.isStreaming = false;
          this.streamStatus = '';
        }
      },

      handleStreamClose() {
        console.log("Stream closed");
        this.resetStreamState();
        // Don't reset userQuery here to preserve the input after streaming

        try {
          if (!this.responseObject.Response) {
            this.responseObject.Response = 'No response received. Please try again.';
          }

          let parsed = null;
          let str = this.fullStringReturn.trim();
          str = '{"' + str;

          console.log('fullStringReturn:', str);
          
          try {
            parsed = JSON.parse(str);
            parsed.image_transcript = this.hold_image_transcript;
            console.log("Parsed:", parsed);
          } catch (err) {
            console.error("Error parsing response:", err);
            try {
              parsed = JSON.parse('{"' + this.fullStringReturn);
              parsed.image_transcript = this.hold_image_transcript;
            } catch (err2) {
              console.error("Error in fallback parsing:", err2);
              parsed = { 
                Response: this.aiResponse.Response || 'Error processing response. Please try again.',
                Error: err2.message
              };
            }
          }

          this.hold_image_transcript = '';
          this.responseObject = parsed;
          
          // Only update aiResponse if we have valid content
          if (parsed && parsed.Response && typeof parsed.Response === 'string') {
            // Preserve the existing response if the parsed one is empty
            if (parsed.Response.trim() === '' && this.aiResponse.Response.trim() !== '') {
              parsed.Response = this.aiResponse.Response;
            }
            this.aiResponse = parsed;
          }
          else{
            this.aiResponse = parsed;
          }
        } catch (error) {
          console.error('Error in stream finish:', error);
          this.aiResponse.Response = `Error processing response: ${error.message}. Please try again.`;
        }
      },

      handleStreamError(error) {
        console.error('Stream error:', error);
        this.resetStreamState();
        
        let errorMessage = 'An error occurred while processing your request.';
        if (error.name === 'AbortError') {
          errorMessage = 'Request was cancelled.';
        } else if (error.name === 'NetworkError') {
          errorMessage = 'Network error. Please check your connection.';
        } else if (error.response) {
          switch (error.response.status) {
            case 429:
              errorMessage = 'Too many requests. Please wait a moment.';
              break;
            case 413:
              errorMessage = 'Image size too large. Please use a smaller image.';
              break;
            default:
              errorMessage = `Server error (${error.response.status}). Please try again.`;
          }
        } else if (error.message) {
          errorMessage = `Error: ${error.message}`;
        }
        
        this.streamStatus = errorMessage;
        this.clearImage();
        this.reset_response();
        // Don't reset userQuery to preserve the input after error
        this.lastRequestTime = Date.now();
        this.retryCount = 0;
      },
  
      generateBookUrl(page) {
        // Implement your URL generation logic here
        return `https://your-pdf-viewer-url/${this.CPG_ID}?page=${page}`;
      },

      //FOR PDF  
            togglePdfViewer(file_page, index) {
        if(!this.CPG_ID){
          this.get_book_data_from_id(this.aiResponse.ID[index])
        }
        this.isPdfOpen = true;
        this.pdfPage = parseInt(file_page);
        if (this.isPdfOpen && this.pdfSource) {
          this.pdfPage = parseInt(file_page);
          //console.log(this.pdfSource, this.pdfPage);
        }
      },

      getBookData(bookId) {
        if (!this.all_books || !this.all_books.book_list) return null;
        
        const book = this.all_books.book_list.find(book => book.id === bookId);
        
        if (book) {
          return {
            id: book.id,
            book_name: book.book_name,
            url: book.url,
            specialty: book.specialty,
            offset: book.offset,
            organization: book.organization,
            country: book.country
          };
        }
        
        return null;
      },

      async get_book_data_from_id(id) {
        this.all_books = await getBooks();
        const bookData = this.getBookData(id);
        if (bookData) {
          this.pdfSource = bookData.url;
        }
      },

      reset_response(){
        console.warn("reset_response()");
        this.full_string = [];
        this.aiResponse = {
          Response: '',
          Filename: {},
          Page: {},
          URL: {},
          ID: {},
          Suggestions: null,
          image_transcript: ''
        };
        this.responseObject = {
          Response: '',
          Filename: {},
          Page: {},
          URL: {},
          ID: {},
          Suggestions: {},
          image_transcript: ''
        };
        this.fullStringReturn = '';
        this.limit_messages = '';
      },

      openFeedback(type) {
        this.$refs.feedbackOverlay.showFeedback(this.Query_UUID, 'cpg/qmed', type);
      },

      handleFeedbackSubmitted(feedbackType) {
        console.log(`Feedback submitted: ${feedbackType}`);
      },

      handleScroll(event) {
        const container = event.target;
        const scrollDiff = container.scrollHeight - (container.scrollTop + container.clientHeight);
        this.showScrollButton = scrollDiff > 100; // Show button when more than 100px from bottom
      },

      scrollToBottom() {
        const container = this.$refs.messageContainer;
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      },

      simulateParseError(){
        console.log("simulating parse error");
        let errorstring = `{ "key" : "id", "key2" : "id2" `;
        errorstring = JSON.parse(errorstring);
      },

      async handleImageUpload(event) {
        this.isImageLoading = true;
        try {
          const file = event.target.files[0];
          if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
              throw new Error('Please upload an image file');
            }
            
            // Check if file is a GIF
            if (file.type === 'image/gif') {
              throw new Error('GIF files are not supported. Please upload a different image format.');
            }
            
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
              throw new Error('Image size should be less than 5MB');
            }

            this.selectedImageFile = file;
            this.selectedImage = URL.createObjectURL(file);
            this.imagePreview = this.selectedImage;
          }
        } catch (error) {
          console.error('Error processing image:', error);
          alert(error.message || 'Error processing image. Please try again.');
          this.clearImage();
        } finally {
          this.isImageLoading = false;
        }
      },

      removeImage() {
        this.clearImage();
      },

      clearImage() {
        if (this.selectedImage) {
          URL.revokeObjectURL(this.selectedImage);
        }
        this.selectedImage = null;
        this.imagePreview = null;
        this.selectedImageFile = null;
        this.isImageLoading = false;
      }
    }
  }
</script>

<template>

  <div class="page-container" :class="{ 'dark-mode': isDarkMode }">

      <NewPortalNotice>
        <template #aside>
          <button class="theme-toggle" @click="toggleTheme" :title="isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'">
            <sun-icon v-if="isDarkMode" class="theme-icon" />
            <moon-icon v-else class="theme-icon" />
          </button>
        </template>
      </NewPortalNotice>

      <!-- Central Search Section -->
      <div class="central-search" :class="{ 'expanded': aiResponse.Response }">
        <div class="search-container">
          <h1 class="main-title" @click="$router.go(0)">Ask CPG</h1>
          <p class="tagline">Gateway to evidence-based answer</p>
    
          <!-- Book Selection Dropdown -->
          <transition name="slide-fade">
              <book-selection v-if="searchType === 'selected'" @book-selected="handleBookSelection" class="mb-3"/>
          </transition>
    
          <!-- Search Input Area -->
          <div class="search-input-wrapper">
            <input
              v-model="userQuery" 
              placeholder="Type in your query..."
              @keyup.enter.exact="handleSubmit"
              :disabled="isLoading"
              class="modern-input"
            ></input>
            <div v-if="!CPG_ID" class="image-upload-container">
              <input
                type="file"
                ref="fileInput"
                accept="image/*"
                @change="handleImageUpload"
                class="hidden"
                id="image-upload"
                :disabled="isImageLoading"
              />
              <label 
                for="image-upload" 
                class="image-upload-btn" 
                :title="selectedImage ? 'Change image' : 'Upload image'"
                :class="{ 'loading': isImageLoading }"
              >
                <i v-if="!isImageLoading" class="fas" :class="selectedImage ? 'fa-image' : 'fa-image'"></i>
                <div v-else class="image-upload-spinner"></div>
              </label>
            </div>
          </div>
    
          <div class="input-controls">
            <div class="dropdown-control">
              <select v-model="searchType" class="cpg-dropdown">
                <option value="all">All CPGs</option>
                <option value="selected">Select and Ask</option>
              </select>
            </div>
            <!-- <book-selection v-if="searchType === 'selected'" @book-selected="handleBookSelection"/> -->
            <button 
              @click="handleSubmit" 
              :disabled="isLoading || !userQuery.trim() || (searchType === 'selected' && !CPG_ID)"
              class="modern-submit-button"
            >
              <div v-if="!isLoading" class="mx-auto">Ask</div>
              <looping-rhombuses-spinner
                v-else
                :animation-duration="2500"
                :rhombus-size="15"
                color="#8252a1"
                class="mx-auto"
              />
            </button>
          </div>
          <!-- Image Preview Card: now below input and controls -->
          <transition name="fade">
            <div v-if="!CPG_ID && imagePreview" class="image-card-preview">
              <div class="image-card">
                <button
                  v-if="!isStreaming"
                  @click="removeImage"
                  class="image-card-close"
                  title="Remove image"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="currentColor" fill-opacity="0.08"/>
                    <path d="M7 7L13 13M13 7L7 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
                <div class="image-card-img-wrapper">
                  <img :src="imagePreview" alt="Preview" class="image-card-img" />
                  <div v-if="isStreaming" class="image-card-overlay">
                    <div class="image-card-spinner"></div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
    
        <!-- Chat Response Section -->
        <transition name="fade">
          <div v-if="aiResponse.Response" class="response-section">
            <div class="chat-container">
              <div class="query-display">
                <h4><b>{{ Query }}</b></h4>
              </div>
              <!-- Render last sent image above markdown output -->
              <div v-if="lastSentImagePreview" class="response-image-card">
                <img :src="lastSentImagePreview" alt="Image sent with last prompt" class="response-image" />
              </div>
              <!-- MedGemma Interpretation Card -->
              <div v-if="aiResponse.image_transcript" class="medgemma-container">
                <button v-if="!showMedGemmaCard" @click="toggleMedGemmaCard" class="medgemma-toggle-btn show-btn">
                  <span class="toggle-icon">▼</span> Show MedGemma Interpretation
                </button>
                <transition name="slide-fade">
                  <div v-if="showMedGemmaCard" class="medgemma-card">
                    <div class="medgemma-header">
                      <span class="medgemma-title">Interpretation from MedGemma</span>
                      <button @click="toggleMedGemmaCard" class="medgemma-toggle-btn hide-btn">
                        Hide<span class="toggle-icon">▲</span>
                      </button>
                    </div>
                    <div class="medgemma-content" v-html="renderedImageTranscript"></div>
                  </div>
                </transition>
              </div>
              
              <div class="messages" ref="messageContainer" @scroll="handleScroll">
                <div class="message ai-message">
                  <div class="message-content markdown-body" v-html="renderedMarkdown"></div>
    
                  <div v-if="Object.keys(aiResponse.Filename || {}).length > 0" class="source-section">
                    <span class="source-label">Source:</span>
                    <div v-for="(filename, key) in aiResponse.Filename" class="source-item my-2">
                      <button @click="togglePdfViewer(aiResponse.Page[key], key)" class="source-button">
                        {{ filename.split('_').join(' ') }} (Page {{ aiResponse.Page[key] }})
                      </button>
                    </div>
                  </div>
    
                  <div v-if="aiResponse.Suggestions" class="suggestions-section">
                    <span class="suggestion-label">Suggestions:</span>
                    <div class="suggestion-chips">
                      <button 
                        v-for="(suggestion, index) in aiResponse.Suggestions" 
                        :key="index"
                        @click="handleSuggestionClick(suggestion)"
                        class="suggestion-chip"
                      >
                        {{ suggestion }}
                      </button>
                    </div>
                  </div>

                  <div class="suggestions-section" v-if="aiResponse.Suggestions">
                    <div class="suggestion-label">What do you think?</div>
                    <div class="feedback-actions">
                      <button class="icon-btn" @click="openFeedback('good')">
                        <i class="fas fa-thumbs-up"></i>
                      </button>
                      <button class="icon-btn" @click="openFeedback('bad')">
                        <i class="fas fa-thumbs-down"></i>
                      </button>
                      <FeedbackOverlay ref="feedbackOverlay" @feedback-submitted="handleFeedbackSubmitted" />
                    </div>
                  </div>
                  

                </div>
    
                <!-- Stream Status -->
                <div v-if="isStreaming" class="stream-status">
                  <looping-rhombuses-spinner
                    :animation-duration="2500"
                    :rhombus-size="15"
                    color="#2cbbb2"
                  />
                  <span>{{ streamStatus }}</span>
                </div>

                  <!-- <button 
                    v-show="showScrollButton" 
                    @click="scrollToBottom"
                    class="scroll-bottom-btn px-2"
                  >
                    <i class="fas fa-arrow-down mx-2"></i> Scroll to Bottom
                  </button> -->
              </div>
            </div>
          </div>
          <div v-else-if="limit_messages" class="response-section">
            <div class="chat-container">
              <div class="messages">
                <div class="message ai-message">
                  <b>{{ limit_messages }}</b>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    
      <footer class="ai-footer">
        <MahtasMark :dark="isDarkMode" />
        <div class="disclaimer-text" style="font-size: 11px; text-align: center; color: #666;">
          AskCPG can make mistakes. Check important information.
        </div>
      </footer>

      <div class="app-version">{{ appVersion }}</div>
  </div>

  <!-- PDF Viewer -->
  <PdfViewer
    v-model:isOpen="isPdfOpen"
    v-model:page="pdfPage"
    :source="pdfSource"
    :page="pdfPage"
  />

</template>

<style>
/* Global styles for dark mode */
:root {
  --light-bg: #ffffff;
  --light-surface: #f8f9fa;
  --light-text: #2c3e50;
  --light-text-secondary: #666666;
  --light-border: #e0e0e0;
  --light-primary: #8252a1;
  --light-primary-hover: #6e2b92;
  --light-secondary: #2cbbb2;
  --light-secondary-hover: #036068;
  --light-overlay: rgba(0, 0, 0, 0.5);
  --light-shadow: rgba(0, 0, 0, 0.05);
  --light-code-bg: #f5f5f5;
  --light-blockquote-bg: #f8f9fa;
  --light-blockquote-border: #0FBAB2;
}

:root.dark-mode {
  --dark-bg: #343541;
  --dark-surface: #444654;
  --dark-text: #ECECF1;
  --dark-text-secondary: #9FA6B1;
  --dark-border: #4B5563;
  --dark-primary: #8252a1;
  --dark-primary-hover: #6e2b92;
  --dark-secondary: #2cbbb2;
  --dark-secondary-hover: #036068;
  --dark-overlay: rgba(0, 0, 0, 0.75);
  --dark-shadow: rgba(0, 0, 0, 0.1);
  --dark-code-bg: #2d2d2d;
  --dark-blockquote-bg: #444654;
  --dark-blockquote-border: #2cbbb2;
}

/* Base styles */
.page-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--light-bg);
  color: var(--light-text);
  transition: all 0.3s ease;
}

.page-container.dark-mode {
  background-color: var(--dark-bg);
  color: var(--dark-text);
}

/* Search container styles */
.search-container {
  text-align: center;
  margin-bottom: 2rem;
  background-color: var(--light-bg);
  padding: 2rem 1rem;
  border-radius: 18px;
  max-width: 800px;
  margin: 0 auto;
}

.dark-mode .search-container {
  background-color: var(--dark-bg);
}

.main-title {
  cursor: pointer;
  font-size: 2.5rem;
  color: var(--light-text);
  margin-bottom: 0.5rem;
  font-weight: 600;
  transition: color 0.2s ease;
}

.main-title:hover {
  color: var(--light-primary);
}

.dark-mode .main-title:hover {
  color: var(--dark-primary);
}

.dark-mode .main-title {
  color: var(--dark-text);
}

.tagline {
  color: var(--light-text-secondary);
  font-size: 1.1rem;
  margin-bottom: 2rem;
}

.dark-mode .tagline {
  color: var(--dark-text-secondary);
}

/* Input styles */
.search-input-wrapper {
  display: flex;
  gap: 0.5rem;
  background: var(--light-bg);
  padding: 0.5rem;
  border-radius: 18px;
  border: 1px solid var(--light-border);
  box-shadow: none;
}

.dark-mode .search-input-wrapper {
  background: #232325;
  border: none;
  box-shadow: none;
}

.modern-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 12px;
  background-color: transparent;
  color: var(--light-text);
  font-size: 1rem;
  transition: all 0.3s ease;
}

.dark-mode .modern-input {
  color: #fff;
}

.modern-input::placeholder {
  color: var(--light-text-secondary);
}

.dark-mode .modern-input::placeholder {
  color: #b3b3b3;
}

/* Chat container styles */
.chat-container {
  background: var(--light-bg);
  border-top: 1px solid var(--light-border);
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.dark-mode .chat-container {
  background: var(--dark-bg);
  border-top-color: var(--dark-border);
}

.message {
  background: var(--light-bg);
  color: var(--light-text);
  padding: 1rem 0.5rem;
  border-radius: 12px;
  margin-bottom: 1rem;
}

.dark-mode .message {
  background: var(--dark-bg);
  color: var(--dark-text);
}

/* Button styles */
.modern-submit-button {
  width: 100px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  background: var(--light-primary);
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dark-mode .modern-submit-button {
  background: var(--dark-primary);
  color: var(--dark-text);
}

.modern-submit-button:hover:not(:disabled) {
  background: var(--light-primary-hover);
}

.dark-mode .modern-submit-button:hover:not(:disabled) {
  background: var(--dark-primary-hover);
}

.modern-submit-button:disabled {
  background: var(--light-text-secondary);
  cursor: not-allowed;
}

.dark-mode .modern-submit-button:disabled {
  background: var(--dark-text-secondary);
  color: var(--dark-text);
}

/* Dropdown styles */
.cpg-dropdown {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid var(--light-border);
  background-color: var(--light-bg);
  color: var(--light-text);
  cursor: pointer;
  font-size: 0.9rem;
  min-width: 120px;
  height: 40px;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%232c3e50' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 1em;
  padding-right: 2.5rem;
}

.dark-mode .cpg-dropdown {
  background-color: var(--dark-surface);
  color: var(--dark-text);
  border-color: var(--dark-border);
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ECECF1' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
}

/* Dropdown options styling */
.cpg-dropdown option {
  background-color: var(--light-bg);
  color: var(--light-text);
  padding: 0.5rem;
}

.dark-mode .cpg-dropdown option {
  background-color: var(--dark-surface);
  color: var(--dark-text);
}

/* Style for the selected option */
.cpg-dropdown option:checked {
  background-color: var(--light-primary);
  color: white;
}

.dark-mode .cpg-dropdown option:checked {
  background-color: var(--dark-primary);
  color: var(--dark-text);
}

/* Style for options when dropdown is open */
.cpg-dropdown option:hover {
  background-color: var(--light-border);
}

.dark-mode .cpg-dropdown option:hover {
  background-color: var(--dark-border);
}

/* Source button styles */
.source-button {
  background: var(--light-surface);
  border: 1px solid var(--light-border);
  color: var(--light-primary);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dark-mode .source-button {
  background: var(--dark-surface);
  border-color: var(--dark-border);
  color: var(--dark-primary);
}

.source-button:hover {
  background: var(--light-primary);
  color: white;
  border-color: var(--light-primary);
}

.dark-mode .source-button:hover {
  background: var(--dark-primary);
  color: var(--dark-text);
  border-color: var(--dark-primary);
}

/* Suggestions section styles */
.suggestions-section {
  border-top: 1px solid var(--light-border);
  margin-top: 1rem;
  padding-top: 1rem;
}

.dark-mode .suggestions-section {
  border-top: 1px solid var(--dark-border);
}

.suggestion-label {
  color: var(--light-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 0.8rem;
  display: block;
}

.dark-mode .suggestion-label {
  color: var(--dark-text-secondary);
}

.suggestion-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
  width: 100%;
}

.suggestion-chip {
  background: #f3f3fa;
  color: var(--light-text);
  border: 1.5px solid #e0e0e0;
  border-radius: 18px;
  padding: 0.65rem 1.2rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.18s, border 0.18s, color 0.18s, box-shadow 0.18s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  width: 100%;
  text-align: left;
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  outline: none;
  user-select: none;
  line-height: 1.4;
}

.suggestion-chip:hover, .suggestion-chip:focus {
  background: #e6e0f7;
  border-color: var(--light-primary);
  color: var(--light-primary);
  box-shadow: 0 2px 8px rgba(130,82,161,0.08);
}

.dark-mode .suggestion-chip {
  background: rgba(255,255,255,0.04);
  color: var(--dark-text);
  border: 1.5px solid #4B5563;
  box-shadow: 0 1px 4px rgba(0,0,0,0.10);
}

.dark-mode .suggestion-chip:hover, .dark-mode .suggestion-chip:focus {
  background: #393950;
  border-color: var(--dark-primary);
  color: var(--dark-primary);
  box-shadow: 0 2px 8px rgba(130,82,161,0.12);
}

@media (max-width: 600px) {
  .suggestion-chips {
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 0.75rem;
    padding-bottom: 0.5rem;
  }
  
  .suggestion-chip {
    width: 100%;
    font-size: 0.95rem;
    padding: 0.8rem 1.2rem;
    border-radius: 12px;
    min-height: 44px;
    display: flex;
    align-items: center;
  }
}

/* Feedback section styles */
.icon-btn {
  background: transparent;
  border: 1px solid var(--light-border);
  border-radius: 6px;
    cursor: pointer;
  padding: 0.5rem;
  transition: all 0.2s ease;
}

.dark-mode .icon-btn {
  border-color: var(--dark-border);
  color: var(--dark-text-secondary);
}

.icon-btn:hover {
  border-color: var(--light-primary);
  color: var(--light-primary);
  background: rgba(130, 82, 161, 0.05);
}

.dark-mode .icon-btn:hover {
  border-color: var(--dark-primary);
  color: var(--dark-primary);
  background: rgba(130, 82, 161, 0.1);
}

/* Input controls container */
.input-controls {
    display: flex;
  justify-content: space-between;
  align-items: center;
    gap: 1rem;
  padding: 0.5rem;
    margin-top: 1rem;
  }
  
/* Mobile styles */
@media (max-width: 768px) {
  .input-controls {
    flex-direction: row; /* Changed from column to row */
    gap: 0.5rem;
    align-items: center;
  }
  
  .cpg-dropdown {
    width: auto; /* Changed from 100% to auto */
    flex: 1; /* Allow dropdown to take remaining space */
  }

  .modern-submit-button {
    width: 80px; /* Slightly smaller on mobile */
  }
}

/* Ensure the select element maintains its background color */
select.cpg-dropdown {
  background-color: var(--light-bg);
}

.dark-mode select.cpg-dropdown {
  background-color: var(--dark-bg);
}

/* Theme toggle styles */
.theme-toggle {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 1000;
  background: rgba(0,0,0,0.06);
  border: 1px solid var(--light-border);
  border-radius: 50%;
  width: 40px;
  height: 40px;
    display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dark-mode .theme-toggle {
  background: rgba(255,255,255,0.06);
  border-color: var(--dark-border);
}

.theme-toggle:hover {
  background: rgba(0,0,0,0.12);
  border-color: var(--light-primary);
}

.dark-mode .theme-toggle:hover {
  background: rgba(255,255,255,0.12);
  border-color: var(--dark-primary);
}

.theme-icon {
  width: 20px;
    height: 20px;
  color: var(--light-text);
}

.dark-mode .theme-icon {
  color: var(--dark-text);
}

/* Responsive styles */
@media (max-width: 768px) {
  .central-search {
    padding-top: 3.25rem;
  }

  .search-container {
    padding: 1rem;
  }
  
  .main-title {
    font-size: 2rem;
  }
  
  .tagline {
    font-size: 1rem;
  }
  
  .chat-container {
    padding: 1rem;
  }
  
  .modern-input {
    font-size: 0.9rem;
  }
  }
</style>
  
<style scoped>
.cpg-container {
  max-width: 100%;
  margin: 0 auto;
  padding: 1rem;
}

.content-wrapper {
  display: flex;
  gap: 2rem;
  margin: 0 auto;
}

.chat-container {
  flex: 1;
  transition: all 0.3s ease;
}

.chat-container.with-pdf {
  max-width: 50%;
}

.pdf-container {
  position: fixed;
  background: white;
  box-shadow: -2px 0 10px rgba(0,0,0,0.1);
  height: 100vh;
  width: 50%;
  right: 0;
  top: 0;
  padding: 1rem;
  z-index: 1000;
}

.pdf-container.mobile {
  width: 100%;
}

.close-pdf {
  position: absolute;
  right: 1rem;
  top: 1rem;
  font-size: 24px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1001;
}

.source-link {
  background: none;
  border: none;
  color: #1976d2;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
}

/* Animation */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .chat-container.with-pdf {
    max-width: 100%;
  }
  
  .pdf-container {
    position: fixed;
    width: 100%;
    height: 100vh;
    left: 0;
  }
}

/* Add your existing styles here */
</style>

<style scoped>
.markdown-body table {
  border-collapse: collapse;
  width: 100%;
  margin: 1rem 0;
}

.markdown-body th,
.markdown-body td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.markdown-body th {
  background-color: #f5f5f5;
}

.source-button {
  background: #f9effa;
  border: none;
  color: #6e2b92;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.source-button:hover {
  background: #6e2b92;
  color: #f9effa;
  transform: translateY(-1px);
}

.suggestions-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0,0,0,0.1);
}

.suggestions-section h4 {
  color: #666;
  margin-bottom: 0.8rem;
  font-weight: 500;
}

.filter-btn {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.2rem;
  background-color: #2cbbb2;
  color: white;
  border: none;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.2s;
}

.filter-btn:hover {
  background-color: #036068;
}
</style>

<style scoped>
/* Add these styles to your existing <style> section */
.ai-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.15rem;
  text-align: center;
  padding: 2rem 1rem 2.4rem;
  margin-top: 0.1rem;
}

@media (max-width: 640px) {
  .ai-footer {
    padding: 0.85rem 0.75rem 1rem;
    gap: 0.45rem;
  }
}

.ai-footer a {
  color: #666;
  text-decoration: none;
  font-size: 1rem;
  transition: color 0.3s ease;
}

.page-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.content-wrapper {
  flex: 1 0 auto; /* Changed to prevent shrinking */
  width: 100%;
  display: flex;
  flex-direction: column;
}

.central-search {
  flex: 1 0 auto; /* Added to prevent shrinking */
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 4.5rem 1rem 2rem;
  transition: all 0.3s ease;
}

.central-search.expanded {
  padding-top: 1.75rem;
}

.ai-footer {
  flex-shrink: 0; /* Prevents footer from shrinking */
  width: 100%;
}

.app-version {
  color: #7b8290;
  font-size: 11px;
  text-align: center;
  padding: 0 0 1rem;
}

</style>

<style scoped>
.input-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
}

.cpg-dropdown {
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  background: #f8f9fa;
  cursor: pointer;
  font-size: 0.9rem;
}

.modern-submit-button {
  width: 100px;
  height: 40px;
  display: flex;
  text-align: center;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  background: #8252a1;
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modern-submit-button:hover {
  background: #6e2b92;
}

.modern-submit-button:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

</style>


<style scoped>
.feedback-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  transition: transform 0.2s ease;
}

.icon-btn:hover {
  transform: scale(1.1);
}

.fa-thumbs-up {
  color: #6e2b92;
}

.fa-thumbs-up:hover {
  color: #180522;
}

.fa-thumbs-down {
  color: #6e2b92;
}

.fa-thumbs-down:hover {
  color: #180522;
}
</style>

<style scoped>
/* Suggestions section styles */
.suggestions-section {
  border-top: 1px solid var(--light-border);
  margin-top: 1rem;
  padding-top: 1rem;
}

.dark-mode .suggestions-section {
  border-top: 1px solid var(--dark-border);
}

.suggestion-label {
  color: var(--light-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 0.8rem;
  display: block;
}

.dark-mode .suggestion-label {
  color: var(--dark-text-secondary);
}

.suggestion-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
}

.suggestion-chip {
  background: #f3f3fa;
  color: var(--light-text);
  border: 1.5px solid #e0e0e0;
  border-radius: 18px;
  padding: 0.65rem 1.2rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.18s, border 0.18s, color 0.18s, box-shadow 0.18s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  width: 100%;
  text-align: left;
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  outline: none;
  user-select: none;
}

.suggestion-chip:hover, .suggestion-chip:focus {
  background: #e6e0f7;
  border-color: var(--light-primary);
  color: var(--light-primary);
  box-shadow: 0 2px 8px rgba(130,82,161,0.08);
}

.dark-mode .suggestion-chip {
  background: rgba(255,255,255,0.04);
  color: var(--dark-text);
  border: 1.5px solid #4B5563;
  box-shadow: 0 1px 4px rgba(0,0,0,0.10);
}

.dark-mode .suggestion-chip:hover, .dark-mode .suggestion-chip:focus {
  background: #393950;
  border-color: var(--dark-primary);
  color: var(--dark-primary);
  box-shadow: 0 2px 8px rgba(130,82,161,0.12);
}

@media (max-width: 600px) {
  .suggestion-chips {
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 0.75rem;
    padding-bottom: 0.5rem;
  }
  
  .suggestion-chip {
    width: 100%;
    font-size: 0.95rem;
    padding: 0.8rem 1.2rem;
    border-radius: 12px;
    min-height: 44px;
    display: flex;
    align-items: center;
  }
}

/* Feedback section styles */
.feedback-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-top: 1rem;
}

.icon-btn {
  background: transparent;
  border: 1px solid var(--light-border);
  border-radius: 6px;
  cursor: pointer;
  padding: 0.5rem;
  transition: all 0.2s ease;
  color: var(--light-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.dark-mode .icon-btn {
  border-color: var(--dark-border);
  color: var(--dark-text-secondary);
}

.icon-btn:hover {
  border-color: var(--light-primary);
  color: var(--light-primary);
  background: rgba(130, 82, 161, 0.05);
}

.dark-mode .icon-btn:hover {
  border-color: var(--dark-primary);
  color: var(--dark-primary);
  background: rgba(130, 82, 161, 0.1);
}

/* Mobile styles for suggestions */
@media (max-width: 768px) {
  .suggestion-chips {
    gap: 0.25rem;
  }
  
  .suggestion-chip {
    padding: 0.4rem 0.8rem;
    font-size: 0.85rem;
  }
}
</style>

<style scoped>
.stream-status {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin: 0.5rem 0 0.5rem 0;
  padding: 0.3rem 0.8rem;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  font-size: 0.98rem;
  color: var(--light-text-secondary);
  font-weight: 500;
  gap: 0.7rem;
}

.dark-mode .stream-status {
  background: transparent;
  color: var(--dark-text-secondary);
  border: none;
  box-shadow: none;
}

.stream-status span {
  margin-top: 0;
  font-size: 0.98rem;
  color: inherit;
  font-weight: 500;
  letter-spacing: 0.01em;
}
</style>

<style scoped>
/* Image Upload Styles */
.image-upload-container {
  display: flex;
  align-items: center;
}

.hidden {
  display: none;
}

.image-upload-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: var(--light-surface);
  color: var(--light-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 8px;
}

.dark-mode .image-upload-btn {
  background: var(--dark-surface);
  color: var(--dark-text-secondary);
}

.image-upload-btn:hover {
  background: var(--light-primary);
  color: white;
}

.dark-mode .image-upload-btn:hover {
  background: var(--dark-primary);
  color: var(--dark-text);
}

.image-preview-container {
  position: relative;
  margin-top: 8px;
  max-width: 200px;
}

.image-preview {
  width: 100%;
  height: auto;
  border-radius: 8px;
  border: 1px solid var(--light-border);
}

.dark-mode .image-preview {
  border-color: var(--dark-border);
}

.remove-image-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--light-surface);
  border: 1px solid var(--light-border);
  color: var(--light-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dark-mode .remove-image-btn {
  background: var(--dark-surface);
  border-color: var(--dark-border);
  color: var(--dark-text-secondary);
}

.remove-image-btn:hover {
  background: var(--light-primary);
  color: white;
  border-color: var(--light-primary);
}

.dark-mode .remove-image-btn:hover {
  background: var(--dark-primary);
  color: var(--dark-text);
  border-color: var(--dark-primary);
}
</style>

<style scoped>
/* Elegant Image Card Preview Styles */
.image-card-preview {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 16px;
}

.image-card {
  position: relative;
  background: var(--light-bg);
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08), 0 1.5px 4px rgba(130,82,161,0.04);
  padding: 16px;
  min-width: 180px;
  max-width: 320px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.2s;
}

.dark-mode .image-card {
  background: var(--dark-surface);
  box-shadow: 0 4px 24px rgba(0,0,0,0.18), 0 1.5px 4px rgba(130,82,161,0.10);
}

.image-card-img {
  max-width: 240px;
  max-height: 180px;
  width: auto;
  height: auto;
  border-radius: 12px;
  object-fit: contain;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  background: #fff;
}

.dark-mode .image-card-img {
  background: #232325;
}

.image-card-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  color: #6e2b92;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: background 0.15s, color 0.15s;
  z-index: 2;
}

.image-card-close:hover {
  background: rgba(130,82,161,0.08);
  color: #2cbbb2;
}

.dark-mode .image-card-close {
  color: #b39ddb;
}

.dark-mode .image-card-close:hover {
  background: rgba(44,187,178,0.10);
  color: #2cbbb2;
}
</style>

<style scoped>
.image-card-img-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  z-index: 2;
}

.dark-mode .image-card-overlay {
  background: rgba(44,44,44,0.5);
}

.image-card-spinner {
  border: 3px solid #e0e0e0;
  border-top: 3px solid #8252a1;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

<style scoped>
.image-card-label {
  margin-top: 12px;
  text-align: center;
  font-size: 0.97rem;
  color: #6e2b92;
  opacity: 0.85;
  letter-spacing: 0.01em;
  width: 100%;
}

.dark-mode .image-card-label {
  color: #b39ddb;
}
</style>

<style scoped>
.image-card.static {
  opacity: 0.85;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  pointer-events: none;
}
</style>

<style scoped>
.response-image-card {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 24px auto;
  padding: 18px 0 8px 0;
  width: 100%;
}

.response-image {
  max-width: 320px;
  max-height: 220px;
  width: auto;
  height: auto;
  border-radius: 14px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.10), 0 1.5px 4px rgba(130,82,161,0.06);
  background: #fff;
  display: block;
}

.dark-mode .response-image {
  background: #232325;
}

/* MedGemma Interpretation Card Styles */
.medgemma-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 720px;
  margin: 0 auto 24px auto;
  padding: 16px 20px;
  background: #1a8681; /*#1EBBB2*/
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  color: white;
}

.dark-mode .medgemma-card {
  color:white;
  background: #1a8681;
  border-color: rgba(130, 82, 161, 0.25);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

.medgemma-header {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.medgemma-title {
  font-family: 'Poppins', sans-serif !important;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.medgemma-logo {
  font-family: 'Poppins', sans-serif !important;
  font-weight: 500;
  display: inline-flex;
  letter-spacing: 0.03em;
  font-size: 1.15rem;
  margin-left: 4px;
  padding: 2px 4px;
  border-radius: 4px;
}

.med-part {
  color: #FFFFFF;
}

.dark-mode .med-part {
  color: #FFFFFF;
}

.gemma-part {
  background: linear-gradient(90deg, #4A90E2 0%, #1E62D0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  font-weight: 600;
  text-shadow: 0 0 2px rgba(74, 144, 226, 0.2);
  filter: drop-shadow(0 0 1px rgba(30, 98, 208, 0.3));
}

.dark-mode .gemma-part {
  background: linear-gradient(90deg, #4A90E2 0%, #1E62D0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  font-weight: 600;
  text-shadow: 0 0 2px rgba(74, 144, 226, 0.2);
  filter: drop-shadow(0 0 1px rgba(30, 98, 208, 0.3));
}

.medgemma-content {
  font-family: 'Poppins', sans-serif !important;
  font-size: 0.95rem;
  line-height: 1.5;
  white-space: pre-wrap;
  letter-spacing: 0.02em;
}

.dark-mode .medgemma-content {
  color: white;
}

.medgemma-container {
  margin: 10px 0;
}

.medgemma-toggle-btn {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
}

.medgemma-toggle-btn.show-btn {
  background: linear-gradient(90deg, #1EBBB2 0%, #1a8681 100%);
  color: white;
  width: 100%;
  margin-bottom: 10px;
  border-radius: 14px;
}

.medgemma-toggle-btn.hide-btn {
  background-color: #105c58;
  color: white;
  margin-left: auto;
  font-size: 0.8rem;
  padding: 4px 8px;
}

.medgemma-toggle-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.medgemma-toggle-btn:active {
  transform: translateY(0);
}

.toggle-icon {
  display: inline-block;
  font-size: 0.7rem;
  margin-right: 4px;
  transition: transform 0.2s ease;
}

.hide-btn .toggle-icon {
  margin-left: 4px;
  margin-right: 0;
}

.dark-mode .medgemma-toggle-btn.hide-btn {
  color: #4A90E2;
}

/* Transition animations */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>

<style scoped>
/* Add loading state styles */
.image-upload-btn.loading {
  cursor: not-allowed;
  opacity: 0.7;
}

.image-upload-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #8252a1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ... existing styles ... */
</style>